// src/components/ui/OnboardingChecklist.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle2, 
  User, 
  FileText, 
  Share2, 
  Link as LinkIcon, 
  Video, 
  Star,
  Copy,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Rocket
} from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';

export function OnboardingChecklist({ userData, videos = [] }) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const steps = [
    { id: 'profilePic', label: 'Profile Pic', completed: !!userData?.profilePic && !userData.profilePic.includes('default-thumbnail'), path: '/profile', icon: User },
    { id: 'bio', label: 'Bio', completed: !!userData?.bio && userData.bio.length >= 20, path: '/profile', icon: FileText },
    { id: 'socialLinks', label: 'Socials', completed: !!userData?.socialLinks && userData.socialLinks.length > 0, path: '/profile', icon: Share2 },
    { id: 'vanityUrl', label: 'Brand Link', completed: !!userData?.vanityId, path: '/profile', icon: LinkIcon },
    { id: 'recordVideo', label: 'Record', completed: videos.length > 0, path: '/record', icon: Video },
    { id: 'setPrimary', label: 'Set Primary', completed: !!userData?.primaryVideoId, path: '/dashboard', icon: Star },
    { id: 'copyLink', label: 'Share Link', completed: !!userData?.hasCopiedLink, path: '/dashboard', icon: Copy }
  ];

  const completedCount = steps.filter(s => s.completed).length;
  const progressPercent = Math.round((completedCount / steps.length) * 100);
  
  // FIXED SVG Math: 
  // Radius 14 fits comfortably inside a 36x36 viewBox with strokeWidth 4
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progressPercent / 100) * circumference;

  if (progressPercent === 100) return null;

  return (
    <Card className="mb-6 border-brand-100 bg-brand-50/20 overflow-hidden shadow-sm">
      <div className="p-3 md:p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-brand-600 text-white rounded-md">
              <Rocket size={16} />
            </div>
            <div>
              <h2 className="text-sm md:text-base font-bold text-gray-900 leading-none">Your Launchpad</h2>
              {!isCollapsed && (
                <p className="text-[10px] md:text-xs text-gray-500 mt-1">Complete these to go live</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 bg-white px-2 py-1 rounded-md border border-brand-100 shadow-sm">
              <span className="text-[10px] font-bold text-brand-700 min-w-[24px] text-right">{progressPercent}%</span>
              <div className="w-9 h-9 flex items-center justify-center relative">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                  {/* Background Circle */}
                  <circle 
                    cx="18" cy="18" r={radius} 
                    fill="none" stroke="currentColor" strokeWidth="4" 
                    className="text-brand-100" 
                  />
                  {/* Progress Circle */}
                  <circle
                    cx="18" cy="18" r={radius}
                    fill="none" stroke="currentColor" strokeWidth="4"
                    className="text-brand-500 transition-all duration-1000 ease-in-out"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </div>
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="h-8 w-8 p-0 text-gray-400 hover:text-brand-600"
            >
              {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </Button>
          </div>
        </div>

        {!isCollapsed && (
          <div className="flex flex-wrap gap-2 mt-4 animate-in fade-in slide-in-from-top-1 duration-200">
            {steps.map((step) => (
              <button
                key={step.id}
                onClick={() => navigate(step.path)}
                className={`
                  flex items-center px-3 py-1.5 rounded-lg border text-[11px] font-bold transition-all
                  ${step.completed 
                    ? 'bg-green-50 border-green-100 text-green-700' 
                    : 'bg-white border-brand-100 text-brand-700 hover:border-brand-300 hover:bg-brand-50 shadow-sm'}
                `}
              >
                <step.icon size={12} className="mr-1.5" />
                {step.label}
                {step.completed && <CheckCircle2 size={12} className="ml-1.5 text-green-600" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
